import { useEffect, useRef, useState } from "react";
import { fetchChatSettings } from "../api/chat";
import { AllResults } from "../components/chat/AllResults";
import { ChatTurnView } from "../components/chat/ChatTurnView";
import { SuggestionChips } from "../components/chat/SuggestionChips";
import { useAsyncData } from "../hooks/useAsyncData";
import { useChat } from "../hooks/useChat";
import type { SearchResponse } from "../api/types";

/** Cards to draw before the settings arrive, and if they never do. Matches the server's default. */
const DEFAULT_RESULTS_SHOWN = 5;

/**
 * The chat surface: ask a question about the organisation's files, in words.
 *
 * The intelligence is not here and not in the backend that serves this — it is the reference agent,
 * talking to the same MCP endpoint Claude or Cursor would, as the signed-in user. This screen draws
 * what that conversation produces.
 *
 * It has one other state: `viewing`, the full result set of a single search shown on its own page.
 * The conversation itself lives in `useChat`, which this component owns, so going to that page and
 * coming back redraws the same turns without refetching the history or re-running the search.
 */
export function ChatScreen(): JSX.Element {
  const { turns, suggestions, examples, busy, ask, stop, reset } = useChat();
  const [draft, setDraft] = useState("");
  const [viewing, setViewing] = useState<SearchResponse | null>(null);
  const foot = useRef<HTMLDivElement>(null);

  // Failure leaves the default in place: a chat that shows five cards is a working chat, and
  // refusing to render the conversation over a preferences lookup would not be.
  const { data: settings } = useAsyncData(() => fetchChatSettings(), []);
  const resultsShown = settings?.results_shown ?? DEFAULT_RESULTS_SHOWN;

  useEffect(() => {
    if (!viewing) {
      foot.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [turns, viewing]);

  const submit = (question: string): void => {
    ask(question);
    setDraft("");
  };

  if (viewing) {
    return <AllResults result={viewing} onBack={() => setViewing(null)} />;
  }

  return (
    <div className="chat-screen">
      <div className="chat-screen__thread">
        {turns.length === 0 && (
          <div className="chat-screen__welcome">
            <h2>Ask about your files</h2>
            <p>
              Search by name and by content across everything you are allowed to open. Results are
              always scoped to one client or project.
            </p>
            <SuggestionChips
              items={examples}
              onPick={submit}
              disabled={busy}
              label="Example questions"
            />
          </div>
        )}

        {turns.map((turn) => (
          <ChatTurnView
            key={turn.id}
            turn={turn}
            resultsShown={resultsShown}
            onViewAll={setViewing}
          />
        ))}
        <div ref={foot} />
      </div>

      <div className="chat-screen__foot">
        <SuggestionChips
          items={suggestions}
          onPick={submit}
          disabled={busy}
          label="Follow-up suggestions"
        />
        <form
          className="chat-screen__composer"
          onSubmit={(event) => {
            event.preventDefault();
            submit(draft);
          }}
        >
          <input
            className="chat-screen__input"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Ask about a client's files, a project, a document…"
            aria-label="Your question"
            autoComplete="off"
          />
          {busy ? (
            <button type="button" className="button button--secondary" onClick={stop}>
              Stop
            </button>
          ) : (
            <button type="submit" className="button button--primary" disabled={!draft.trim()}>
              Ask
            </button>
          )}
        </form>
        <div className="chat-screen__tools">
          <button
            type="button"
            className="link-button"
            onClick={() => void reset()}
            disabled={busy || turns.length === 0}
          >
            Clear this conversation
          </button>
        </div>
      </div>
    </div>
  );
}
